import { useEffect, useState } from 'react';
import { Check, ChevronRight, Clock, AlertCircle, CircleCheck, Circle, Zap, X, Unlock } from 'lucide-react';
import { format, formatDistanceStrict } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { api } from '../../api/client';

function fmtDuracao(segs) {
  if (!segs || segs < 60) return `${segs || 0}s`;
  const mins = Math.floor(segs / 60);
  if (mins < 60) return `${mins}m`;
  const horas = Math.floor(mins / 60);
  if (horas < 48) return `${horas}h`;
  const dias = Math.floor(horas / 24);
  if (dias < 30) return `${dias}d`;
  return `${Math.floor(dias / 30)} mês${Math.floor(dias / 30) > 1 ? 'es' : ''}`;
}

export function FunilDemanda({ demandaId, isComunicacao, isAdmin, onChanged }) {
  const [funil, setFunil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);
  const [erro, setErro] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/demandas/${demandaId}/funil`);
      setFunil(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [demandaId]);

  const mudar = async (destino, force = false) => {
    if (!destino) return;
    setSaving(true);
    setErro(null);
    try {
      const body = { etapa: destino };
      if (force) body.force = true;
      await api.post(`/demandas/${demandaId}/etapa`, body);
      setForceOpen(false);
      await load();
      onChanged?.();
    } catch (e) {
      setErro({ mensagem: e.message, dados: e.data });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !funil) {
    return <div className="card"><div className="card-body center" style={{ minHeight: 120 }}><span className="spinner" /></div></div>;
  }

  const proxima = funil.proxima_acao;
  const transicaoSugerida = proxima.destino ? funil.transicoes_validas.find((t) => t.destino === proxima.destino) : null;
  const bloqueada = transicaoSugerida && !transicaoSugerida.ok;
  const prerequisitosAtivos = transicaoSugerida?.prerequisitos || [];

  return (
    <div className="card">
      <div className="card-body" style={{ padding: 0 }}>
        {/* Stepper */}
        <div className="funil-stepper">
          {funil.stages.map((s, i) => {
            const isCancelled = funil.cancelada;
            const baseColor = s.status === 'passou' ? 'var(--c-success)'
              : s.status === 'atual' ? 'var(--c-primary)'
              : 'var(--c-border-strong)';
            return (
              <div key={s.key} className={`funil-step funil-step-${s.status}`}>
                <div className="funil-step-dot" style={{ borderColor: baseColor, background: s.status === 'passou' ? baseColor : s.status === 'atual' ? 'white' : 'white' }}>
                  {s.status === 'passou' ? <Check size={14} color="white" /> :
                    s.status === 'atual' ? <Zap size={14} color={baseColor} /> :
                    <Circle size={8} color={baseColor} fill={baseColor} />}
                </div>
                <div className="funil-step-label">
                  <div className="funil-step-title">{s.label}</div>
                  {s.entrou_em && s.status !== 'futuro' && (
                    <div className="funil-step-time">
                      {s.status === 'atual' ? (
                        <>há {formatDistanceStrict(new Date(s.entrou_em), new Date(), { locale: ptBR })}</>
                      ) : (
                        fmtDuracao(s.segundos_total)
                      )}
                    </div>
                  )}
                </div>
                {i < funil.stages.length - 1 && (
                  <div className="funil-step-connector" style={{ background: s.status === 'passou' ? 'var(--c-success)' : 'var(--c-border)' }} />
                )}
              </div>
            );
          })}
          {funil.cancelada && (
            <div className="funil-step funil-step-cancelada">
              <div className="funil-step-dot" style={{ borderColor: 'var(--c-danger)', background: 'var(--c-danger)' }}>
                <X size={14} color="white" />
              </div>
              <div className="funil-step-label"><div className="funil-step-title">Cancelada</div></div>
            </div>
          )}
        </div>

        {/* Próxima ação sugerida */}
        <div className="funil-next">
          <div className="funil-next-left">
            <div className="funil-next-label">
              {bloqueada ? <AlertCircle size={14} /> : <ChevronRight size={14} />} Próxima ação
            </div>
            <div className="funil-next-text">{proxima.texto || '—'}</div>
          </div>
          {isComunicacao && proxima.cta && proxima.destino && (
            <button
              className={`btn ${bloqueada ? 'btn-secondary' : 'btn-primary'} btn-lg`}
              onClick={() => mudar(proxima.destino)}
              disabled={saving || bloqueada}
              title={bloqueada ? 'Requisitos obrigatórios pendentes' : ''}
            >
              {proxima.cta} <ChevronRight size={16} />
            </button>
          )}
        </div>

        {/* Requisitos */}
        {prerequisitosAtivos.length > 0 && (
          <div className="funil-checklist">
            <div className="funil-checklist-title">
              Checklist para {transicaoSugerida.destino_label}
            </div>
            {prerequisitosAtivos.map((r) => (
              <div key={r.key} className={`funil-check-item ${r.ok ? 'ok' : r.required ? 'required' : 'warning'}`}>
                {r.ok ? <CircleCheck size={16} /> : r.required ? <AlertCircle size={16} /> : <Circle size={16} />}
                <span>{r.label}</span>
                {!r.ok && r.required && <span className="funil-badge-required">obrigatório</span>}
              </div>
            ))}
          </div>
        )}

        {/* Transições disponíveis */}
        {isComunicacao && (funil.transicoes_validas.length > 1 || isAdmin) && (
          <div className="funil-actions">
            <div className="funil-actions-label">Mover para outra etapa:</div>
            <div className="row-wrap" style={{ gap: 6 }}>
              {funil.transicoes_validas
                .filter((t) => t.destino !== proxima.destino || !proxima.cta)
                .map((t) => (
                  <button
                    key={t.destino}
                    className="btn btn-secondary btn-sm"
                    onClick={() => mudar(t.destino)}
                    disabled={saving || !t.ok}
                    title={t.erro || ''}
                  >
                    {t.destino_label}
                  </button>
                ))}
              {isAdmin && (
                <button className="btn btn-ghost btn-sm" onClick={() => setForceOpen((v) => !v)} title="Forçar etapa (admin)">
                  <Unlock size={12} /> Forçar…
                </button>
              )}
            </div>

            {forceOpen && isAdmin && (
              <div className="funil-force">
                <div className="text-xs muted" style={{ marginBottom: 6 }}>⚠️ Forçar ignora pré-requisitos e pode levar a qualquer etapa. Use com cautela.</div>
                <div className="row-wrap" style={{ gap: 6 }}>
                  {['nova_demanda', 'aprovada', 'em_captacao', 'em_edicao', 'aguardando_aprovacao', 'pronta_para_publicar', 'publicado', 'cancelada'].map((e) => (
                    <button key={e} className="btn btn-ghost btn-sm" onClick={() => mudar(e, true)} disabled={saving}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {erro && (
          <div style={{ padding: 14, background: 'var(--c-danger-soft)', color: '#7f1d1d', fontSize: 13, borderTop: '1px solid var(--c-border)' }}>
            ❌ {erro.mensagem}
            {erro.dados?.requer_force && isAdmin && (
              <div style={{ marginTop: 6 }}>
                <em>Use "Forçar…" para prosseguir mesmo assim.</em>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
